import React, { useLayoutEffect, useState } from "react";
import sha256 from "crypto-js/sha256";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import TextField from "@mui/material/TextField";
import { InputAdornment, Link, Stack } from "@mui/material";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import { useAuth } from "../../../utils/useAuth/useAuth";
import { postAuth } from "../../../utils/api";
import s from "./Login.module.scss";
import c from "lib/style/colorVar.scss";

const PAGE_LOCALE_KEY = "page.login.";

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [visibility, setVisibility] = useState(false);
  const [isAccountTouched, setIsAccountTouched] = useState(false);
  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const [isLoginProcessing, setIsLoginProcessing] = useState(false);
  const [isLoginFailed, setIsLoginFailed] = useState(false);
  const [isServerError, setIsServerError] = useState(false);

  useLayoutEffect(() => {
    localStorage.clear();
  }, []);

  const handleAccountTyping = (e) => {
    setAccount(e.target.value);
    setIsAccountTouched(true);
    setIsLoginFailed(false);
    setIsServerError(false);
  };

  const handlePasswordTyping = (e) => {
    setPassword(e.target.value);
    setIsPasswordTouched(true);
    setIsLoginFailed(false);
    setIsServerError(false);
  };

  const handleChangeVisibility = () => {
    if (!visibility) {
      setVisibility(true);
    } else {
      setVisibility(false);
    }
  };

  const loginMasterPortal = async () => {
    // validation: try to login without account and password
    if (!account && !password) {
      setIsAccountTouched(true);
      setIsPasswordTouched(true);
      return;
    }
    // validation: try to login without account only
    if (!account) {
      setIsAccountTouched(true);
      return;
    }
    // validation: try to login without password only
    if (!password) {
      setIsPasswordTouched(true);
      return;
    }
    // if username and password are input as normal, the error message should be disappear
    setIsLoginFailed(false);
    setIsServerError(false);
    setIsLoginProcessing(true);
    try {
      const hashPsd = sha256(password).toString();
      const response = await postAuth("/login", {
        email: account,
        password: hashPsd,
      });
      setIsLoginProcessing(false);
      if (!response || !response?.data) {
        setIsServerError(true);
        return;
      }
      const responseBody = response.data;
      switch (responseBody.responseCode) {
        case "0000": {
          login(responseBody.data);
          break;
        }
        case "4000": {
          // OTP workflow
          navigate("/user/otp-verify", {
            state: {
              email: account,
              password: hashPsd,
            },
          });
          break;
        }
        case "4010": {
          // OTP Wrong username or password
          setIsLoginFailed(true);
          break;
        }
        case "4001": {
          // Send OTP failed
          setIsServerError(true);
          break;
        }
        default: {
          setIsServerError(true);
          break;
        }
      }
    } catch (error) {
      setIsServerError(true);
    }
  };

  const usernameHelperText = () => {
    if (isAccountTouched && !account) {
      return t(PAGE_LOCALE_KEY + "fields.email.helperText");
    }
    return " ";
  };

  const passwordHelperText = () => {
    if (isPasswordTouched && !password) {
      return t(PAGE_LOCALE_KEY + "fields.password.helperText");
    }
    return " ";
  };

  const loginButton = () => {
    return (
      <ButtonMAS
        variant="auth-button"
        isLoading={isLoginProcessing}
        disabled={isLoginProcessing}
        className={s.loginBtn}
        onClick={loginMasterPortal}
      >
        {t(PAGE_LOCALE_KEY + "button.login")}
      </ButtonMAS>
    );
  };

  const loginFailMessage = () => {
    let failMessage;
    if (isLoginFailed) {
      failMessage = t(PAGE_LOCALE_KEY + "message.loginFail");
    } else if (isServerError) {
      failMessage = t(PAGE_LOCALE_KEY + "message.serverError");
    } else {
      failMessage = "";
    }
    return (
      failMessage && <span className={s.loginFailMessage}>{failMessage}</span>
    );
  };

  return (
    <div className={s.textField}>
      <p className={s.title}>{t(PAGE_LOCALE_KEY + "label.title")}</p>
      <Box
        component={s.form}
        sx={{ "& .MuiTextField-root": { m: 0.5, width: 1 } }}
        autoComplete="off"
      >
        <div className={s.input}>
          <TextField
            disabled={isLoginProcessing}
            label={t(PAGE_LOCALE_KEY + "fields.email.label")}
            InputLabelProps={{ shrink: true }}
            onChange={handleAccountTyping}
            helperText={usernameHelperText()}
            FormHelperTextProps={{ sx: { color: "red" } }}
            variant="standard"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                loginMasterPortal();
              }
            }}
            sx={{
              "& .MuiInput-root": {
                height: "40px",
              },
            }}
          />
        </div>
        <div className={s.input}>
          <TextField
            disabled={isLoginProcessing}
            type={visibility ? "text" : "password"}
            label={t(PAGE_LOCALE_KEY + "fields.password.label")}
            InputLabelProps={{ shrink: true }}
            onChange={handlePasswordTyping}
            helperText={passwordHelperText()}
            FormHelperTextProps={{ sx: { color: "red" } }}
            variant="standard"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                loginMasterPortal();
              }
            }}
            sx={{
              "& .MuiInput-root": {
                height: "40px",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <ButtonMAS
                    variant="text-only"
                    disabled={isLoginProcessing}
                    onClick={handleChangeVisibility}
                    style={{
                      minHeight: 32,
                      maxHeight: 32,
                      minWidth: 32,
                      maxWidth: 32,
                      padding: 0,
                      borderRadius: 16,
                    }}
                  >
                    {visibility ? (
                      <VisibilityIcon
                        sx={{ fontSize: "18px", color: "#666" }}
                      />
                    ) : (
                      <VisibilityOffIcon
                        sx={{ fontSize: "18px", color: "#666" }}
                      />
                    )}
                  </ButtonMAS>
                </InputAdornment>
              ),
            }}
          />
        </div>
        {loginButton()}
        {loginFailMessage()}
        <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
          <Link
            variant="button-normal"
            color={c["brand1"]}
            underline="none"
            href=""
            onClick={() => {
              navigate("/user/forget-password");
            }}
          >
            {t(PAGE_LOCALE_KEY + "link.forgetPassword")}
          </Link>
        </Stack>
      </Box>
    </div>
  );
};

export default Login;
